/* eslint-disable max-lines */
import chunk from 'lodash/chunk';
import groupBy from 'lodash/groupBy';
import keyBy from 'lodash/keyBy';
import sumBy from 'lodash/sumBy';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  penumbra,
  type PenumbraFile,
  type PenumbraZipWriter,
  type RemoteResource,
} from '@transcend-io/penumbra';

import {
  buildUseLazyQuery,
  buildUseMutation,
  convertPenumbraJsonFilesToCsv,
  logger,
  PRIVACY_CENTER_STREAMSAVER_ENDPOINT,
} from '@main/core-ui';
import { DownloadFileOrderField } from '@main/dsr-types';
import type { DownloadStatusesLookup } from '@main/pc-core-components';
import { endpoints } from '@main/pc-types';
import type { ID } from '@main/schema-utils';
import { OrderDirection } from '@main/schema-utils';

import { useDownloadZipMessages } from './messages';
import type { DownloadZipFn } from './types';
import { useCheckSombraScopesError } from './useCheckSombraScopesError';
import { useDiffieHellman } from './useDiffieHellman';

// we are limited by s3 to 6 streams
const MAX_CONCURRENT_DOWNLOADS = 5;
const META_DATA_PAGE_SIZE = 10;

/**
 * Download file
 */
type DownloadFile = RemoteResource & {
  /** File size */
  size: number;
  /** File name */
  fileName: string;
  /** requestFile id */
  id: ID<'requestFile'>;
};

/**
 * If debugging is enabled, we will log debug information to the console
 */
class DebugLogger {
  private debug: boolean;

  /**
   * Initializes a DebugLogger
   *
   * @param debug - If true, will log debug information to the console
   */
  constructor(debug: boolean) {
    this.debug = debug;

    // Configure Penumbra to log debug information to the console as well
    if (debug) {
      penumbra.setLogLevel(0);
    }
  }

  /**
   * Log debug information to the console
   *
   * @param message - The message to log
   */
  logDebug(message: string): void {
    if (!this.debug) {
      return;
    }
    const header = `[useDownloadZip]`.padEnd(29, ' ');
    logger.debug(header, message);
  }
}

/** Mutation to download CEK context */
const useDataSubjectDownloadCEK = buildUseMutation(
  endpoints.dataSubjectDownloadCEK,
  'DownloadAccessZip',
);

/** Fetch a page of files */
const useDownloadFiles = buildUseLazyQuery(
  endpoints.downloadFiles,
  'PcDownloadZipFiles',
  {
    nodes: {
      id: null,
      fileName: null,
      title: {
        id: null,
        defaultMessage: null,
        description: null,
      },
      size: null,
      mimetype: null,
      authTag: null,
      ignoreAuthTag: null,
      iv: null,
    },
    totalSize: null,
  },
);

const useRequestFileDownloadURLs = buildUseLazyQuery(
  endpoints.downloadFiles,
  'DownloadZipRequestFileURLs',
  {
    nodes: {
      id: null,
      downloadUrl: null,
    },
  },
);

const useDownloadFilesCountsOnly = buildUseLazyQuery(
  endpoints.downloadFilesCountsOnly,
  'PcDownloadZipFilesCountsOnly',
  {
    nodes: {
      dataPointId: null,
      name: null,
      title: {
        id: null,
        defaultMessage: null,
        description: null,
      },
      fileCount: null,
    },
  },
);

/**
 * Gets remote files, downloads and - if necessary - decrypts them, then saves them as a .zip
 *
 * @returns A function that will download the zip and the progress indicator
 */
export function useDownloadZip(): [DownloadZipFn, DownloadStatusesLookup] {
  const [createRequestFileDhEncrypted] = useDiffieHellman();
  const [createDhEncrypted, decryptResponse] = useDiffieHellman();
  const checkSombraScopesError = useCheckSombraScopesError();

  /** Lazy query to page over files in zip */
  const getDownloadFiles = useDownloadFiles();
  const getDownloadFilesCountsOnly = useDownloadFilesCountsOnly();
  const getRequestFileDownloadURLs = useRequestFileDownloadURLs();

  /** Mutation to get the CEK for decrypting files */
  const [dataSubjectDownloadCEK, { error: dataSubjectDownloadCEKError }] =
    useDataSubjectDownloadCEK();

  /** For internationalization */
  const { formatMessage } = useIntl();

  /** Save progress of zip */
  const [downloadStatuses, setDownloadStatuses] =
    useState<DownloadStatusesLookup>({});

  useEffect(() => {
    if (dataSubjectDownloadCEKError) {
      checkSombraScopesError(dataSubjectDownloadCEKError.message);
    }
  }, [dataSubjectDownloadCEKError]);

  const getPageURLs = useCallback(
    async (
      ids: Array<ID<'requestFile'>>,
      requestId: ID<'request'>,
    ): Promise<
      Array<{
        /** id of the returned request file */
        id: ID<'requestFile'>;
        /** fresh signed url for download */
        url: string;
      }>
    > => {
      if (!createRequestFileDhEncrypted) {
        throw new Error('Could not authenticate');
      }

      const { data } = await getRequestFileDownloadURLs({
        dhEncrypted: createRequestFileDhEncrypted(),
        filterBy: {
          requestId,
          ids,
        },
      });
      if (!data) {
        throw new Error(`failed to fetch download URL for ${ids.join(', ')}`);
      }
      return data.nodes.map((rf) => ({
        id: rf.id,
        url: rf.downloadUrl,
      }));
    },
    [useRequestFileDownloadURLs],
  );
  const getFilePage = useCallback(
    async ({
      requestId,
      offset,
      unwrappedCEK,
    }: {
      /** id of request */
      requestId: ID<'request'>;
      /** page offset */
      offset: number;
      /** auth key */
      unwrappedCEK: Buffer;
    }): Promise<[DownloadFile[], number]> => {
      // No dh channel established
      if (!createRequestFileDhEncrypted) {
        throw new Error('Could not authenticate');
      }

      const { data, error } = await getDownloadFiles({
        first: META_DATA_PAGE_SIZE,
        offset,
        dhEncrypted: createRequestFileDhEncrypted(),
        filterBy: { requestId },
        orderBy: [
          // we sort by size to more efficiently use our chunked connections
          {
            field: DownloadFileOrderField.Size,
            direction: OrderDirection.DESC,
          },
          {
            field: DownloadFileOrderField.CreatedAt,
            direction: OrderDirection.DESC,
          },
        ],
      });
      if (error) {
        checkSombraScopesError(error.message);
      }
      if (!data) {
        throw new Error(`Fetching page "${offset}" failed`);
      }

      return [
        data.nodes.map((downloadFile) => ({
          id: downloadFile.id,
          path: downloadFile.fileName,
          url: downloadFile.downloadUrl,
          mimetype: downloadFile.mimetype,
          size: +downloadFile.size,
          decryptionOptions: {
            authTag: downloadFile.authTag,
            iv: downloadFile.iv,
            key: unwrappedCEK,
          },
          fileName: downloadFile.fileName,
          ignoreAuthTag: downloadFile.ignoreAuthTag,
        })),
        +data.totalSize,
      ];
    },
    [createRequestFileDhEncrypted],
  );

  const getFilePageCountsOnly: (requestId: ID<'request'>) => Promise<
    {
      /** ID of dataPoint */
      dataPointId: string;
      /** Name of dataPoint */
      name: string;
      /** Translated title of dataPoint */
      title?: string;
      /** The number of files found for datapoint */
      fileCount: number;
    }[]
  > = useCallback(
    async (requestId) => {
      // No dh channel established
      if (!createRequestFileDhEncrypted) {
        throw new Error('Could not authenticate');
      }

      const { data, error } = await getDownloadFilesCountsOnly({
        dhEncrypted: createRequestFileDhEncrypted(),
        filterBy: { requestId },
      });

      if (error) {
        checkSombraScopesError(error.message);
      }

      return data.nodes.map((downloadFile) => ({
        dataPointId: downloadFile.dataPointId,
        name: downloadFile.name,
        title: downloadFile.title
          ? formatMessage(downloadFile.title)
          : undefined,
        fileCount: downloadFile.fileCount,
      }));
    },
    [createRequestFileDhEncrypted],
  );

  return [
    async (requestId, { report, zipFileName, convertJSONtoCSV = false }) => {
      // @ts-expect-error - Debug flag for Penumbra downloads
      const debug = !!window.PENUMBRA_DEBUG;
      const debugLogger = new DebugLogger(debug);
      const logDebug = debugLogger.logDebug.bind(debugLogger);

      logDebug(
        `Starting download of request ID: ${requestId} with convertJSONtoCSV=${convertJSONtoCSV}`,
      );

      setDownloadStatuses((lookup) => ({
        ...lookup,
        [requestId]: {
          isDownloading: true,
          progress: 0,
        },
      }));

      // No dh channel established
      if (!createDhEncrypted) {
        setDownloadStatuses((lookup) => ({
          ...lookup,
          [requestId]: {
            isDownloading: false,
            progress: 0,
          },
        }));
        throw new Error('Could not authenticate');
      }

      // Download the CEK to decrypt the files
      const { data } = await dataSubjectDownloadCEK({
        variables: { id: requestId, dhEncrypted: createDhEncrypted() },
      });
      if (!data?.decryptionContext) {
        setDownloadStatuses((lookup) => ({
          ...lookup,
          [requestId]: {
            isDownloading: false,
            progress: 0,
          },
        }));
        throw new Error(
          'An error occurred while authenticating for zip download',
        );
      }

      const unwrappedResponse = decryptResponse(data.decryptionContext);

      // fetch all file metadata up front minus signed urls
      let totalFetched = 0;

      const allFileMetadata: DownloadFile[] = [];
      let totalSize = 0;

      let latestFoundPageSize: null | number = null;
      // keep fetching metadata until no more files found
      /* eslint-disable no-await-in-loop */
      while (latestFoundPageSize !== 0) {
        const [files, fileSize] = await getFilePage({
          requestId,
          offset: totalFetched,
          unwrappedCEK: unwrappedResponse,
        });

        allFileMetadata.push(...files);
        totalSize += fileSize;

        // Keep track of what was processed
        totalFetched += files.length;
        latestFoundPageSize = files.length;
      }

      logDebug(
        `Finished collating metadata for ${totalFetched} files. Total bytes to download: ${totalSize}`,
      );

      // Page by file names to help with concatenating files to CSV
      const groupedFiles = groupBy(allFileMetadata, 'fileName');
      const groupedFileChunks = Object.entries(groupedFiles).flatMap(
        ([fileName, fileGroup]) =>
          chunk(fileGroup, MAX_CONCURRENT_DOWNLOADS).map((fileGroupPage) => ({
            fileName,
            chunkSizeBytes: sumBy(fileGroupPage, 'size'),
            subFilesInChunk: fileGroupPage,
          })),
      );

      // Initialize zip writer
      const writer: PenumbraZipWriter = penumbra.saveZip({
        streamSaverEndpoint: PRIVACY_CENTER_STREAMSAVER_ENDPOINT,
        name: zipFileName,
        size: totalSize,
        /**
         * Progress handler
         *
         * @param event - Zip progress emit
         */
        onProgress({ detail: { percent } }) {
          if (typeof percent === 'number' && !Number.isNaN(percent)) {
            setDownloadStatuses((lookup) => ({
              ...lookup,
              [requestId]: {
                isDownloading: true,
                progress: percent,
              },
            }));
          }
        },
      });

      try {
        /**
         * Iterate through each of the page, which is already divided by file name
         * and then write them to the zip
         */
        for await (const {
          chunkSizeBytes,
          subFilesInChunk,
          fileName,
        } of groupedFileChunks) {
          logDebug(
            `Processing file ${fileName} ${subFilesInChunk.length} sub files and total size ${chunkSizeBytes} bytes`,
          );

          if (typeof writer.byteSize === 'number') {
            writer.byteSize += chunkSizeBytes;
          } else {
            writer.byteSize = chunkSizeBytes ?? null;
          }
          const downloadURLs = await getPageURLs(
            subFilesInChunk.map((f) => f.id),
            requestId,
          );

          logDebug(
            `Fetched ${downloadURLs.length} download URLs for ${subFilesInChunk.length} subFiles files`,
          );

          const urlsByRequestFileId = keyBy(downloadURLs, 'id');

          const fileObjectsForPenumbra: (RemoteResource & DownloadFile)[] =
            subFilesInChunk.map((fileMetadata) => ({
              ...fileMetadata,
              url: urlsByRequestFileId[fileMetadata.id]?.url,
              ignoreAuthTag: true,
            }));

          const missingURLs = fileObjectsForPenumbra.filter(
            (file) => !file.url,
          );
          logDebug(
            `Found ${missingURLs.length} files missing download URLs ${missingURLs.map((f) => f.id).join(', ')}`,
          );

          // For each file, add it to this folder in the zip
          const penumbraFileStreams = await penumbra.get(
            ...fileObjectsForPenumbra,
          );

          logDebug(
            // eslint-disable-next-line max-len
            `Penumbra has started downloading from ${downloadURLs.length} download URLs for ${fileObjectsForPenumbra.length} files`,
          );

          const filesToWrite: PenumbraFile[] = [];
          if (!convertJSONtoCSV) {
            filesToWrite.push(...penumbraFileStreams);
          } else {
            filesToWrite.push(
              ...(await convertPenumbraJsonFilesToCsv(penumbraFileStreams, {
                groupFilesByPath: true,
                logDebug,
              })),
            );
          }

          logDebug(
            `Writing ${filesToWrite.length} files to zip with total size ${chunkSizeBytes} bytes`,
          );

          await writer.write(...filesToWrite);

          logDebug(
            `Done writing ${filesToWrite.length} files to zip with total size ${chunkSizeBytes} bytes`,
          );
        }
        /* eslint-enable no-await-in-loop */

        // Getting COUNTS_ONLY files
        const fileCounts = await getFilePageCountsOnly(requestId);
        await Promise.all(
          fileCounts.map((file) =>
            writer.write({
              path: `${file.name}.txt`,
              mimetype: 'text/plain',
              stream: new Response(
                formatMessage(useDownloadZipMessages.countsOnlyMessage, {
                  count: file.fileCount,
                  title: file.title ?? file.name,
                }),
              ).body!,
            }),
          ),
        );

        // Add the HTML report
        await writer.write({
          path: 'report.html',
          mimetype: 'text/html',

          stream: new Response(report).body!,
        });
        setDownloadStatuses((lookup) => ({
          ...lookup,
          [requestId]: {
            isDownloading: false,
            progress: 100,
          },
        }));

        logDebug(`Calling close() on writer.`);

        // All writes are done, so we can close the writer
        await writer.close();

        logDebug(`Writer is done.`);
      } catch (error) {
        logger.error(
          new Error(useDownloadZipMessages.downloadError.defaultMessage, {
            cause: error,
          }),
        );
        // Abort the writer (if not already aborted) so the browser's native download spinner also stops
        await writer.abort(error);
        // Clear the download state
        setDownloadStatuses({});
        // Throw an internationalized error message
        const errorMessage = formatMessage(
          useDownloadZipMessages.downloadError,
        );
        throw new Error(errorMessage, { cause: error });
      }
    },
    downloadStatuses,
  ];
}
/* eslint-enable max-lines */
